import { computed, inject, Injector, Signal, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { map, Observable } from 'rxjs'

export interface EntitySignalStore<T extends { id: ID }, ID extends string | number = number> {
	entities: Signal<T[]>
	activeId: Signal<ID | undefined>
	activeId$: Observable<ID | undefined>
	active: Signal<T | undefined>
	active$: Observable<T | undefined>
	get(id: ID): T | undefined
	getMany(ids: ID[]): T[]
	getAll(): T[]
	has(id: ID): boolean
	select(id: ID): Observable<T | undefined>
	selectMany(ids: ID[]): Observable<T[]>
	selectAll(): Observable<T[]>
	set(items: T[]): void
	add(item: T): void
	upsert(items: T | T[]): void
	update(id: ID, changes: Partial<T>): void
	remove(id: ID | ID[]): void
	setActive(id: ID | undefined): void
}

/**
 * Native-signals replacement for a `@ngneat/elf` store built with `withEntities()` (+ optional `withActiveId()`).
 * Every entity type here is keyed by its own `id` field, matching how every repository in this codebase used elf.
 */
export function createEntitySignalStore<T extends { id: ID }, ID extends string | number = number>(): EntitySignalStore<
	T,
	ID
> {
	// Captured here (a valid injection context, since this factory is always called from a field initializer
	// or constructor) so the `$` observables below can be built with `toObservable()` regardless of where in
	// the app this store ends up being constructed.
	const injector = inject(Injector)

	// Elf kept entities in a plain `Record<id, T>`, where property access coerces keys to strings - so a
	// route param like `"3"` transparently matched an entity whose `id` is the number `3`. Every ID here is
	// normalized to a string for lookups/comparisons to preserve that loose matching; the signals/methods
	// still expose the original `ID` type (number or string) as declared by each repository.
	const key = (id: ID): string => String(id)

	const entities = signal<T[]>([])
	const activeId = signal<ID | undefined>(undefined)
	const entities$ = toObservable(entities, { injector })
	const activeId$ = toObservable(activeId, { injector })

	const byId = computed(() => new Map(entities().map((item) => [key(item.id), item] as const)))
	const active = computed(() => {
		const id = activeId()
		return id === undefined ? undefined : byId().get(key(id))
	})
	const active$ = toObservable(active, { injector })

	const get = (id: ID) => byId().get(key(id))
	const getMany = (ids: ID[]) => ids.map((id) => byId().get(key(id))).filter((item): item is T => item !== undefined)

	return {
		entities: entities.asReadonly(),
		activeId: activeId.asReadonly(),
		activeId$,
		active,
		active$,
		get,
		getMany,
		getAll: () => entities(),
		has: (id) => byId().has(key(id)),
		// Derived from the already-built `entities$` via a plain `map()`, rather than creating a fresh
		// `computed()`/`toObservable()` (i.e. a fresh `effect()`) on every call - `select`/`selectMany` can be
		// invoked from anywhere, including synchronously from inside another `effect()`'s callback, where
		// creating a new effect would throw NG0602 ("effect() cannot be called from within a reactive context").
		select: (id) => entities$.pipe(map(() => byId().get(key(id)))),
		selectMany: (ids) => entities$.pipe(map(() => getMany(ids))),
		selectAll: () => entities$,
		set: (items) => entities.set(items),
		add: (item) => entities.update((current) => [...current, item]),
		upsert: (items) => {
			const upserted = Array.isArray(items) ? items : [items]
			entities.update((current) => {
				const merged = new Map(current.map((item) => [key(item.id), item] as const))
				for (const item of upserted) merged.set(key(item.id), item)
				return Array.from(merged.values())
			})
		},
		update: (id, changes) => {
			entities.update((current) =>
				current.map((item) => (key(item.id) === key(id) ? { ...item, ...changes } : item)),
			)
		},
		remove: (ids) => {
			const removed = new Set((Array.isArray(ids) ? ids : [ids]).map(key))
			entities.update((current) => current.filter((item) => !removed.has(key(item.id))))
		},
		setActive: (id) => activeId.set(id),
	}
}
